<script lang="ts">
  /**
   * How everything is running — the health detail the landing page deliberately
   * reduced to one sentence.
   *
   * Everything here was on the landing page before and is genuinely useful; it was
   * just useful to somebody *investigating*, which is not who is typing the address
   * at seven in the morning. Somebody who navigates here has gone looking, so this
   * screen answers in full: per-portal state, latency, the synthetic probe, how many
   * sweeps a failure has survived, and when it was last checked.
   *
   * The four states are kept distinct because collapsing them is what makes a
   * dashboard untrustworthy. "Live but slow" is not "fine" and it is not "down";
   * "never checked" is not "down" either — it is the honest answer for a portal
   * whose health URL has never been registered, and reporting it as an outage would
   * teach the household to ignore the whole column.
   */
  import { Icon, Panel } from "@steyncd/steyn-design";
  import { estate, host, ICON, money, type Portal } from "../lib/estate.svelte";

  /** Relative time, because "14:32" tells you nothing without today's date. */
  function ago(iso: string | null): string {
    if (!iso) return "—";
    const ms = Date.now() - Date.parse(iso);
    if (!Number.isFinite(ms)) return "—";
    const m = Math.round(ms / 60000);
    if (m < 1) return "just now";
    if (m < 60) return `${m} min ago`;
    const h = Math.round(m / 60);
    if (h < 24) return `${h} h ago`;
    return `${Math.round(h / 24)} d ago`;
  }

  const checked = $derived(estate.visible.filter((p) => p.status === "live" && p.health));
  /**
   * Slowest first, then by name. Sorting by what is worst puts the thing worth
   * reading at the top; alphabetical order would bury a failing portal at H.
   */
  const ranked = $derived.by(() =>
    [...estate.visible].sort((a, b) => {
      const rank = (p: Portal) =>
        p.status !== "live" ? 3 : !p.health ? 2 : !p.health.ok ? 0 : p.health.slow ? 1 : 1.5;
      return rank(a) - rank(b) || a.name.localeCompare(b.name);
    }),
  );
</script>

<div class="wrap">
  <header class="head">
    <h1>How everything is running</h1>
    <p class="sub">
      Fabric sweeps every portal's health URL every five minutes. A portal that registers a
      synthetic probe is also asked to do the thing it exists for, not merely to answer.
    </p>
  </header>

  <!-- The figures the old hero carried. They belong to somebody investigating, so
       they live here, and each says what it cannot answer rather than showing a zero. -->
  <div class="figs">
    <div class="fig">
      <div class="fig__n num">{estate.liveCount}</div>
      <div class="fig__l">responding</div>
    </div>
    <div class="fig">
      <div class="fig__n num" class:fig__n--warn={estate.downCount > 0}>{estate.downCount}</div>
      <div class="fig__l">not responding</div>
    </div>
    <div class="fig">
      <div class="fig__n num" class:fig__n--warn={estate.slowCount > 0}>{estate.slowCount}</div>
      <div class="fig__l">answering slowly</div>
    </div>
    <div class="fig">
      <!-- Dash, never zero. The route answers with an explicit note before the
           billing export exists, and a confident R0 is the one figure nobody
           should believe. -->
      <div class="fig__n num">{estate.spend ? money(estate.spend) : "—"}</div>
      <div class="fig__l">this month{#if !estate.spend}, not readable{/if}</div>
    </div>
  </div>

  <section class="card">
    <Panel
      loading={estate.loading && !estate.loaded}
      error={estate.error}
      isEmpty={estate.loaded && ranked.length === 0}
      onretry={() => estate.load()}
      rows={6}
    >
      {#snippet empty()}
        No portal is registered yet. Fabric's registry is the source for this screen —
        seed it with <code>npm run seed</code> in the fabric repo.
      {/snippet}
    <div class="scroll">
      <table>
        <thead>
          <tr>
            <th>Portal</th>
            <th>State</th>
            <th class="r">Latency</th>
            <th>Probe</th>
            <th class="r">Failures</th>
            <th class="r">Checked</th>
          </tr>
        </thead>
        <tbody>
          {#each ranked as p (p.id)}
            <tr>
              <td>
                <span class="nm"><Icon name={ICON[p.id] ?? "door"} size={14} /> {p.name}</span>
                <a class="addr num" href={p.url}>{host(p.url)}</a>
              </td>
              <td data-label="State">{@render state(p)}</td>
              <td class="r num sub" data-label="Latency">{p.health?.ok ? `${p.health.latencyMs} ms` : "—"}</td>
              <td class="sub" data-label="Probe">
                {#if p.health?.probeOk === true}
                  <span class="ok"><Icon name="check" size={11} /> answered</span>
                {:else if p.health?.probeOk === false}
                  <span class="warn" title={p.health.probeDetail ?? ""}>
                    <Icon name="alert" size={11} /> failed
                  </span>
                {:else}
                  <span class="quiet">none registered</span>
                {/if}
              </td>
              <td class="r num sub" data-label="Failures">
                {p.health && p.health.consecutiveFailures > 0 ? p.health.consecutiveFailures : "—"}
              </td>
              <td class="r sub" data-label="Checked">{ago(p.health?.checkedAt ?? null)}</td>
            </tr>
          {/each}
        </tbody>
      </table>
    </div>

    {#if checked.length === 0 && !estate.loading}
      <p class="sub pad">
        <Icon name="pause" size={12} />
        No portal has been swept yet. That means the sweep has not run, not that the estate is down.
      </p>
    {/if}
    </Panel>
  </section>
</div>

{#snippet state(p: Portal)}
  <!-- Glyph, word and colour on every state. Never colour alone. -->
  {#if p.status !== "live"}
    <span class="pill quiet"><Icon name="pause" size={11} /> {p.status}</span>
  {:else if p.health === null}
    <span class="pill quiet"><Icon name="pause" size={11} /> never checked</span>
  {:else if p.health.ok && p.health.slow}
    <span class="pill warn" title={p.health.probeDetail ?? ""}><Icon name="clock" size={11} /> live · slow</span>
  {:else if p.health.ok}
    <span class="pill ok"><Icon name="check" size={11} /> live</span>
  {:else}
    <span class="pill warn" title={p.health.probeDetail ?? ""}><Icon name="alert" size={11} /> not responding</span>
  {/if}
{/snippet}

<style>
  .wrap {
    display: flex;
    flex-direction: column;
    gap: 16px;
    padding-bottom: 40px;
  }
  .head {
    padding: 22px 0 0;
  }
  h1 {
    margin: 0 0 6px;
    font-size: 1.5rem;
    font-weight: 800;
    letter-spacing: -0.7px;
    color: var(--tx);
  }
  .head .sub {
    max-width: 62ch;
    font-size: 0.7812rem;
    line-height: 1.5;
  }

  .figs {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(140px, 1fr));
    gap: 10px;
  }
  .fig {
    padding: 14px 16px;
    border-radius: var(--r-card, 14px);
    background: var(--s1);
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .fig__n {
    font-size: 1.5625rem;
    font-weight: 800;
    letter-spacing: -0.8px;
    color: var(--tx);
  }
  .fig__n--warn {
    color: var(--warn);
  }
  .fig__l {
    margin-top: 3px;
    font-size: 0.72rem;
    font-weight: 700;
    color: var(--mut);
  }

  .card {
    padding: 6px 4px 4px;
  }
  .scroll {
    /* Wide tables scroll inside their own box; the page body must never scroll
       sideways. */
    overflow-x: auto;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    font-size: 0.7812rem;
  }
  th {
    padding: 9px 12px;
    text-align: left;
    font-size: 0.72rem;
    font-weight: 800;
    letter-spacing: 0.4px;
    text-transform: uppercase;
    color: var(--mut);
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
  }
  td {
    padding: 10px 12px;
    border-bottom: 1px solid var(--line);
    vertical-align: top;
  }
  tr:last-child td {
    border-bottom: none;
  }
  .r {
    text-align: right;
  }
  .nm {
    display: inline-flex;
    align-items: center;
    gap: 7px;
    font-weight: 700;
    color: var(--tx);
  }
  .addr {
    display: block;
    margin-top: 2px;
    font-size: 0.72rem;
    color: var(--mut);
  }
  .addr:hover {
    color: var(--acc);
  }

  .pill {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    padding: 3px 9px;
    border-radius: var(--r-pill);
    font-size: 0.72rem;
    font-weight: 800;
    white-space: nowrap;
    box-shadow: inset 0 0 0 1px var(--line);
  }
  .ok {
    color: var(--ok);
  }
  .warn {
    color: var(--warn);
  }
  .quiet {
    color: var(--mut);
  }
  .ok,
  .warn,
  .quiet {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    white-space: nowrap;
  }
  .pad {
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 12px;
  }

  /* Under 700px a six-column table cannot be read by scrolling sideways, so each
     row becomes a stacked block with its column name as a label. `content` from a
     data-attribute rather than duplicated markup, so there is one source for the
     header text and no chance of the two disagreeing. */
  @media (max-width: 700px) {
    thead {
      display: none;
    }
    tr {
      display: block;
      padding: var(--sp-3, 12px) 0;
      border-bottom: 1px solid var(--line);
    }
    td {
      display: flex;
      justify-content: space-between;
      gap: var(--sp-3, 12px);
      padding: 3px 0;
      border: none;
      text-align: right;
    }
    td::before {
      content: attr(data-label);
      font-size: var(--fs-micro);
      font-weight: 800;
      letter-spacing: 0.4px;
      text-transform: uppercase;
      color: var(--mut);
      text-align: left;
    }
    td:first-child {
      display: block;
      text-align: left;
      margin-bottom: var(--sp-1, 4px);
    }
    td:first-child::before {
      content: none;
    }
  }
</style>
